/**
 * Single source of truth for the Pulpit vendor / partner onboarding.
 *
 * Two phases:
 *   Phase 1  quick lead + mobile OTP  (screens/PartnerNew/HeroPartner)
 *   Phase 2  full resumable wizard     (screens/PartnerOnboarding)
 *
 * Everything static the two phases share lives here so the form, the review
 * screen, and the API payload never drift apart.
 */

import { getCityStateCode } from "./cityStateMap"

/* ------------------------------------------------------------------ */
/*  Cab types                                                          */
/* ------------------------------------------------------------------ */

// value is what we send to the backend; label is what the vendor sees.
export const CAB_TYPES = [
  { value: "hatchback", label: "Hatchback", example: "Swift, WagonR", seats: 4 },
  { value: "sedan", label: "Sedan", example: "Dzire, Etios", seats: 4 },
  { value: "suv", label: "SUV", example: "Ertiga, Innova", seats: 6 },
  { value: "innova", label: "Innova / MUV", example: "Innova Crysta", seats: 7 },
  { value: "tempo", label: "Tempo Traveller", example: "9 to 17 seater", seats: 12 },
]

export const cabTypeLabel = (value) =>
  CAB_TYPES.find((c) => c.value === value)?.label || value

/* ------------------------------------------------------------------ */
/*  Business types  (drives which documents are required)              */
/* ------------------------------------------------------------------ */

export const BUSINESS_TYPES = [
  {
    value: "individual",
    label: "Individual Driver",
    hint: "You own and drive one or more cabs yourself.",
    gstRequired: false,
    incorporationRequired: false,
  },
  {
    value: "proprietor",
    label: "Proprietor / Travel Agency",
    hint: "A registered travel business or sole proprietor with a small fleet.",
    gstRequired: false,
    incorporationRequired: false,
  },
  {
    value: "company",
    label: "Company / LLP",
    hint: "A Pvt Ltd, LLP or registered company operating a fleet.",
    gstRequired: true,
    incorporationRequired: true,
  },
]

export const businessType = (value) =>
  BUSINESS_TYPES.find((b) => b.value === value) || BUSINESS_TYPES[0]

/* ------------------------------------------------------------------ */
/*  Indian states  (+ auto-fill from city)                             */
/* ------------------------------------------------------------------ */

// Mirrors the state codes documented in cityStateMap.js so we can turn the
// numeric code that helper returns back into a state name for the form.
const STATE_CODE_TO_NAME = {
  1: "Jammu and Kashmir",
  2: "Himachal Pradesh",
  3: "Punjab",
  4: "Chandigarh",
  5: "Uttarakhand",
  6: "Haryana",
  7: "Delhi",
  8: "Rajasthan",
  9: "Uttar Pradesh",
  10: "Bihar",
  13: "Nagaland",
  14: "Manipur",
  15: "Mizoram",
  16: "Tripura",
  17: "Meghalaya",
  18: "Assam",
  19: "West Bengal",
  20: "Jharkhand",
  21: "Odisha",
  22: "Chhattisgarh",
  23: "Madhya Pradesh",
  24: "Gujarat",
  27: "Maharashtra",
  28: "Andhra Pradesh",
  29: "Karnataka",
  32: "Kerala",
  33: "Tamil Nadu",
  34: "Puducherry",
  35: "Andaman and Nicobar Islands",
}

// Full list for the dropdown (superset of what the code map covers).
export const INDIAN_STATES = [
  "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam",
  "Bihar", "Chandigarh", "Chhattisgarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir",
  "Jharkhand", "Karnataka", "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha",
  "Puducherry", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
  "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
]

/** Best-effort auto-fill of the State field from a chosen city. */
export const getStateForCity = (city) => {
  if (!city) return ""
  const code = getCityStateCode(city)
  return STATE_CODE_TO_NAME[code] || ""
}

/* ------------------------------------------------------------------ */
/*  Cities offered in the Phase 1 quick form                           */
/* ------------------------------------------------------------------ */

export const PARTNER_CITIES = [
  "Delhi NCR", "Mumbai", "Bangalore", "Hyderabad", "Chennai", "Kolkata",
  "Pune", "Ahmedabad", "Surat", "Jaipur", "Lucknow", "Chandigarh",
  "Goa", "Indore", "Bhopal", "Nagpur", "Vadodara", "Rajkot",
  "Kochi", "Coimbatore", "Madurai", "Visakhapatnam", "Vijayawada",
  "Mysuru", "Mangaluru", "Hubli", "Nashik", "Aurangabad",
  "Agra", "Varanasi", "Prayagraj", "Kanpur", "Meerut", "Gorakhpur",
  "Noida", "Gurugram", "Faridabad", "Ghaziabad",
  "Udaipur", "Jodhpur", "Kota",
]

/* ------------------------------------------------------------------ */
/*  Rate card  (hybrid model: per-km base + optional fixed routes)     */
/* ------------------------------------------------------------------ */

// One set of these numbers per cab type the vendor offers. Together they let
// the fare engine compute a fare for ANY route, so the vendor never has to
// fill thousands of city pairs.
export const RATE_FIELDS = [
  { key: "baseFare", label: "Base fare", unit: "₹", hint: "Minimum fare for a trip", min: 0 },
  { key: "perKmOneWay", label: "Per km (one way)", unit: "₹/km", hint: "Charged on the run distance", min: 0, required: true },
  { key: "perKmRoundTrip", label: "Per km (round trip)", unit: "₹/km", hint: "Usually lower than one way", min: 0 },
  { key: "driverAllowance", label: "Driver allowance", unit: "₹/day", hint: "Bata per day", min: 0 },
  { key: "nightCharge", label: "Night charge", unit: "₹", hint: "10pm to 6am, optional", min: 0 },
  { key: "minKmPerDay", label: "Min km / day", unit: "km", hint: "Outstation minimum, e.g. 250", min: 0 },
]

// The optional "top routes" table: the vendor's key city-to-city one-way fares.
export const ROUTE_RATE_COLUMNS = ["fromCity", "toCity", "cabType", "oneWayFare"]

/* ------------------------------------------------------------------ */
/*  Documents  (list adapts to business type)                          */
/* ------------------------------------------------------------------ */

// appliesTo: which business types must/should provide it.
// required: hard requirement to submit (for the business types in appliesTo).
const DOC = (key, label, group, required, appliesTo, accept = "image/*,application/pdf") =>
  ({ key, label, group, required, appliesTo, accept })

export const DOCUMENTS = [
  // Identity / driver
  DOC("dl", "Commercial Driving License (LMV / Transport badge)", "Driver & identity", true, ["individual", "proprietor", "company"]),
  DOC("aadhaar", "Aadhaar Card", "Driver & identity", true, ["individual", "proprietor", "company"]),
  DOC("pan", "PAN Card (for TDS / Form 16A)", "Driver & identity", true, ["individual", "proprietor", "company"]),
  DOC("photo", "Passport-size photograph", "Driver & identity", false, ["individual", "proprietor", "company"], "image/*"),
  DOC("police", "Police Verification Certificate", "Driver & identity", false, ["individual", "proprietor", "company"]),

  // Vehicle
  DOC("rc", "Registration Certificate (RC) - not older than 8 years", "Vehicle", true, ["individual", "proprietor", "company"]),
  DOC("permit", "All India Tourist Permit or state outstation permit", "Vehicle", true, ["individual", "proprietor", "company"]),
  DOC("insurance", "Commercial vehicle insurance (comprehensive)", "Vehicle", true, ["individual", "proprietor", "company"]),
  DOC("puc", "Pollution Under Control (PUC) certificate", "Vehicle", false, ["individual", "proprietor", "company"]),
  DOC("fitness", "Fitness Certificate", "Vehicle", false, ["individual", "proprietor", "company"]),
  DOC("vehiclePhotos", "Vehicle photos - front, back, interior (AC)", "Vehicle", false, ["individual", "proprietor", "company"], "image/*"),

  // Business / tax
  DOC("gst", "GST Registration Certificate", "Business & tax", true, ["company"]),
  DOC("gstOptional", "GST Registration Certificate (if registered)", "Business & tax", false, ["proprietor"]),
  DOC("incorporation", "Certificate of Incorporation / CIN", "Business & tax", true, ["company"]),
  DOC("tradeLicense", "Trade License / Shop & Establishment", "Business & tax", false, ["proprietor", "company"]),
  DOC("authLetter", "Authorization letter for each fleet vehicle", "Business & tax", false, ["proprietor", "company"]),

  // Banking
  DOC("bankProof", "Cancelled cheque / passbook (for payouts)", "Banking", true, ["individual", "proprietor", "company"], "image/*,application/pdf"),
]

/** Documents that apply to a given business type, grouped for display. */
export const documentsForBusiness = (type) => {
  const applicable = DOCUMENTS.filter((d) => d.appliesTo.includes(type))
  const groups = {}
  applicable.forEach((d) => {
    groups[d.group] = groups[d.group] || []
    groups[d.group].push(d)
  })
  return groups
}

/* ------------------------------------------------------------------ */
/*  Wizard steps                                                       */
/* ------------------------------------------------------------------ */

export const WIZARD_STEPS = [
  { id: "business", title: "Business type", short: "Type" },
  { id: "details", title: "Business & tax details", short: "Details" },
  { id: "fleet", title: "Your fleet", short: "Fleet" },
  { id: "rates", title: "Rate card", short: "Rates" },
  { id: "documents", title: "Documents", short: "Docs" },
  { id: "bank", title: "Bank & payout", short: "Bank" },
  { id: "review", title: "Review & submit", short: "Review" },
]
